/**
 * «Почему вы сойдётесь» (P-12, BS §11.4) — объяснение подбора пары.
 *
 * Показывает не «оценку человека», а совпадение двоих: полосу и счёт,
 * причины по убыванию вклада, общие и родственные интересы, варианты
 * первой фразы. На mobile живёт в листе поверх колоды, на desktop — в
 * боковой панели рядом с ней; содержимое одно.
 */
import type { Interest, UserCard, Vibe } from '@shared/api/types'
import { Chip, Icon, cx } from '@shared/ui'

import { CompatMeter } from './CompatMeter'
import styles from './VibePanel.module.css'
import { reasonIcon } from './vibe'

interface VibePanelProps {
  vibe: Vibe
  card: UserCard
  /** Интересы viewer'а — чтобы назвать «мою» половину родственной пары. */
  myInterests: readonly Interest[]
  /** Копировать фразу; без обработчика фразы показываются как текст. */
  onCopyIcebreaker?: (text: string) => void
  className?: string
}

export function VibePanel({
  vibe,
  card,
  myInterests,
  onCopyIcebreaker,
  className,
}: VibePanelProps): React.ReactElement {
  const interestsById = new Map((card.interests ?? []).map((i) => [i.id, i]))
  const mineById = new Map(myInterests.map((i) => [i.id, i]))
  const shared = vibe.shared_interest_ids
    .map((id) => interestsById.get(id))
    .filter((i): i is Interest => i !== undefined)
  const related = vibe.related_pairs
    .map((pair) => ({ mine: mineById.get(pair.mine), theirs: interestsById.get(pair.theirs) }))
    .filter(
      (pair): pair is { mine: Interest; theirs: Interest } =>
        pair.mine !== undefined && pair.theirs !== undefined,
    )
  const maxStrength = Math.max(0.01, ...vibe.reasons.map((r) => r.strength))

  return (
    <div className={cx(styles['panel'], className)}>
      {/*
        Одна сводка на панель: подпись полосы стоит у самого счёта, поэтому
        отдельная строка «кое-что общее уже есть» только повторяла её.
      */}
      <div className={styles['hero']}>
        <CompatMeter vibe={vibe} size="lg" />
      </div>

      <blockquote className={styles['headline']}>
        <Icon name="sparkle" size={18} />
        <span>{vibe.headline}</span>
      </blockquote>

      {vibe.reasons.length ? (
        <section className={styles['section']}>
          <h3 className={styles['sectionTitle']}>Из чего складывается</h3>
          <ul className={styles['reasons']}>
            {vibe.reasons.map((reason) => (
              <li key={`${reason.kind}:${reason.label}`} className={styles['reason']}>
                <span className={styles['reasonIcon']}>
                  <Icon name={reasonIcon(reason.kind)} size={18} />
                </span>
                <span className={styles['reasonBody']}>
                  <span className={styles['reasonLabel']}>{reason.label}</span>
                  <span className={styles['bar']} aria-hidden>
                    <span
                      className={styles['barFill']}
                      style={{ width: `${Math.round((reason.strength / maxStrength) * 100)}%` }}
                    />
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {shared.length ? (
        <section className={styles['section']}>
          <h3 className={styles['sectionTitle']}>Общие интересы</h3>
          <div className={styles['chips']}>
            {shared.map((interest) => (
              <Chip key={interest.id} static selected icon="check">
                {interest.label}
              </Chip>
            ))}
          </div>
        </section>
      ) : null}

      {related.length ? (
        <section className={styles['section']}>
          <h3 className={styles['sectionTitle']}>Близкие интересы</h3>
          <ul className={styles['pairs']}>
            {related.map((pair) => (
              <li key={`${pair.mine.id}:${pair.theirs.id}`} className={styles['pair']}>
                <span className={styles['pairSide']}>{pair.mine.label}</span>
                <span className={styles['pairLink']} role="img" aria-label="похоже на">
                  <Icon name="link" size={16} />
                </span>
                <span className={styles['pairSide']}>{pair.theirs.label}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {vibe.icebreakers.length ? (
        <section className={styles['section']}>
          <h3 className={styles['sectionTitle']}>С чего начать</h3>
          <ul className={styles['icebreakers']}>
            {vibe.icebreakers.map((text) =>
              onCopyIcebreaker ? (
                <li key={text}>
                  <button
                    type="button"
                    className={styles['icebreaker']}
                    onClick={() => onCopyIcebreaker(text)}
                    aria-label={`Скопировать: ${text}`}
                  >
                    <span className={styles['icebreakerText']}>{text}</span>
                    <span className={styles['icebreakerCopy']} aria-hidden>
                      <Icon name="copy" size={16} />
                    </span>
                  </button>
                </li>
              ) : (
                <li key={text} className={styles['icebreakerStatic']}>
                  {text}
                </li>
              ),
            )}
          </ul>
          <p className={styles['note']}>Пригодится после взаимного «Го».</p>
        </section>
      ) : null}

      <p className={styles['footnote']}>
        Считается по интересам, темам, форматам и времени обеих сторон.
        {vibe.source === 'assisted' ? ' Формулировку подобрала языковая модель.' : ''}
      </p>
    </div>
  )
}
